// Supplier variant-coverage modal — opened from a row action in
// FamilySuppliersPanel. Lists ONLY the variants this supplier covers
// (`coveredVariantIds`), each with full details: label (brand · spec ·
// packagingSummary, same composition as the workspace grid), Origin, State,
// Packaging, Spec, and Active tag. `coveredVariantIds` can reference variants
// that no longer exist in `variants` (e.g. deleted since the supplier last
// synced its catalog) — those render as a single trailing "#id (deleted)"
// row, so the count in the panel's tooltip always reconciles with what's
// shown here.

import type { MasterProduct } from 'apis/master-products';
import {
  Box,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalHeader,
  ModalOverlay,
  Table,
  Tag,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
} from 'design-system';
import { useMemo } from 'react';
import { packagingSummary, variantLabel } from './packaging';

export interface SupplierCoverageModalProps {
  isOpen: boolean;
  onClose: () => void;
  supplierName: string;
  coveredVariantIds: number[];
  variants: MasterProduct[];
}

export const SupplierCoverageModal = ({
  isOpen,
  onClose,
  supplierName,
  coveredVariantIds,
  variants,
}: SupplierCoverageModalProps) => {
  // O(1) lookups instead of `variants.find(...)`/`.includes(...)` per row.
  const variantsById = useMemo(() => new Map(variants.map((v) => [v.id, v])), [variants]);
  const coveredVariants = useMemo(
    () => coveredVariantIds.map((id) => variantsById.get(id)).filter((v): v is MasterProduct => !!v),
    [coveredVariantIds, variantsById]
  );
  const deletedCoveredIds = useMemo(
    () => coveredVariantIds.filter((id) => !variantsById.has(id)),
    [coveredVariantIds, variantsById]
  );

  // 5xl: the six-column variant table needs the width — at 'lg' it clipped
  // into a horizontal scroll with most columns hidden.
  return (
    <Modal isOpen={isOpen} onClose={onClose} size={{ base: 'full', md: '5xl' }} scrollBehavior='inside'>
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>{supplierName || 'Supplier'} — variant coverage</ModalHeader>
        <ModalCloseButton />
        <ModalBody pb='6'>
          {coveredVariants.length === 0 && deletedCoveredIds.length === 0 ? (
            <Text color='gray.500' fontSize='sm' py='2'>
              This supplier has no linked variants.
            </Text>
          ) : (
            <Box overflowX='auto'>
              <Table size='sm' variant='simple'>
                <Thead>
                  <Tr>
                    <Th>Variant</Th>
                    <Th>Origin</Th>
                    <Th>State</Th>
                    <Th>Packaging</Th>
                    <Th>Spec</Th>
                    <Th>Active</Th>
                  </Tr>
                </Thead>
                <Tbody>
                  {coveredVariants.map((variant) => (
                    <Tr key={variant.id}>
                      <Td>{variantLabel(variant)}</Td>
                      <Td>{variant.origin || '—'}</Td>
                      <Td>{variant.state || '—'}</Td>
                      <Td>
                        {packagingSummary({
                          baseUnit: variant.baseUnit ?? '',
                          itemUnit: variant.itemUnit ?? '',
                          netContent: variant.netContent != null ? String(variant.netContent) : '',
                          packUnit: variant.packUnit ?? '',
                          packSize: variant.packSize != null ? String(variant.packSize) : '',
                        })}
                      </Td>
                      <Td>{variant.spec || '—'}</Td>
                      <Td>
                        <Tag size='sm' colorScheme={variant.isActive ? 'green' : 'gray'}>
                          {variant.isActive ? 'Active' : 'Inactive'}
                        </Tag>
                      </Td>
                    </Tr>
                  ))}
                  {deletedCoveredIds.map((id) => (
                    <Tr key={`deleted-${id}`}>
                      <Td colSpan={6}>
                        <Text as='span' color='gray.500'>
                          #{id} (deleted)
                        </Text>
                      </Td>
                    </Tr>
                  ))}
                </Tbody>
              </Table>
            </Box>
          )}
        </ModalBody>
      </ModalContent>
    </Modal>
  );
};
